import { Injectable } from "@angular/core";
import * as firebase from "firebase/app";
import "firebase/messaging";
import { environment } from "environments/environment";
import { resource } from "@util/resource";
import { combineLatest, forkJoin, Observable, of, ReplaySubject, Subject } from "rxjs";
import { AccountsService } from "services/accounts.service";
import { filter, map, switchMap, take, tap } from "rxjs/operators";
import { Notification } from "@interfaces/notifications";
import { lazyToast } from "@web/util/toast/toast";

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private USE_NEW_ENDPOINT = true;
  private USE_OLD_ENDPOINT = true;

  clientId: string | undefined = undefined;

  token$: ReplaySubject<string> = new ReplaySubject<string>(1);

  notification$: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
  messages$: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);

  totalUnreadedNotifications$: ReplaySubject<number> = new ReplaySubject<number>(1);
  totalUnreadedMessages$: ReplaySubject<number> = new ReplaySubject<number>(1);

  /** Emitted after a message-banner is dismissed (X) so the shell can hide the top banner. */
  private readonly messageBannerDismissedSubject = new Subject<string>();
  readonly messageBannerDismissed$ = this.messageBannerDismissedSubject.asObservable();

  constructor(private accountsService: AccountsService) {
    this.clientId = "DWS_WEB_" + new Date().getTime().toString();

    // Initialize with empty arrays to avoid null
    this.notification$.next([]);
    this.messages$.next([]);

    accountsService.account$.subscribe((account) => {
      if (account) {
        this.initializeFirebaseEnvironment().then(() => {
          console.info("user registered for notifications");
        });
      }
    });
  }

  private async initializeFirebaseEnvironment() {
    const workerPath = environment.values["fcm-worker"];
    const swRegistration = await navigator.serviceWorker.register(workerPath);

    const vapidKey = environment.values.firebaseAuthKey;
    this.clientId = "DWS_WEB_" + vapidKey;
    firebase.initializeApp(environment.values.firebaseConfig);

    const messaging = firebase.messaging();

    try {
      const token = await messaging.getToken({
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (token) {
        const expirationDate = new Date(Date.now() + 1000 * 60 * 60 * 24 * 270);
        this.registerDevice(token, expirationDate).subscribe();
      }

      messaging.onMessage((message) => {
        // console.log("Message received:", message);
        if (message?.data?.type === "notification") {
          lazyToast(`${message.notification.title} - ${message.notification.body}`, 10000, "bottom");
        } else {
          this.list().subscribe();
          this.listMexs().subscribe();
        }
      });
    } catch (err) {
      console.error("Error getting notification token:", err);
    }
  }

  private registerDevice(token: string, expirationDate: Date) {
    if (this.USE_NEW_ENDPOINT) {
      return resource("crm://notification-tokens/create-renew").post({
        token,
        expireAt: expirationDate,
        userAgent: navigator.userAgent,
        clientId: this.clientId,
      });
    } else {
      return resource("v2://notification-tokens").post({ token });
    }
  }

  list(page: number = 1, size: number = 20, show_unreaded: boolean = false, show_readed: boolean = false): Observable<Notification[]> {
    const fetchNew = this.USE_NEW_ENDPOINT
      ? this.fetchNotificationsFromEndpoint("crm://notification/notifications/list", page, size, show_unreaded, show_readed)
      : of({ data: [], unreaded_number: 0 });

    /*
    const fetchOld = this.USE_OLD_ENDPOINT
      ? this.fetchNotificationsFromEndpoint(
          "v2://host/push-notifications",
          page,
          size,
          show_unreaded,
          show_readed
        )
      : of({ data: [], unreaded_number: 0 });
    */

    return this.mergeNotificationsResult(this.notification$, this.totalUnreadedNotifications$, fetchNew, of({ data: [], unreaded_number: 0 }), page);
  }

  listMexs(page: number = 1, size: number = 10, show_unreaded: boolean = false, show_readed: boolean = false): Observable<Notification[]> {
    const fetchNew = this.USE_NEW_ENDPOINT
      ? this.fetchNotificationsFromEndpoint("crm://notification/messages/list", page, size, show_unreaded, show_readed)
      : of({ data: [], unreaded_number: 0 });

    
    const fetchOld = this.USE_OLD_ENDPOINT
      ? this.fetchNotificationsFromEndpoint("v2://host/push-notifications/mex", page, size, show_unreaded, show_readed)
      : of({ data: [], unreaded_number: 0 });
    
   
    return this.mergeNotificationsResult(this.messages$, this.totalUnreadedMessages$, fetchNew, fetchOld, page);
  }

  private mergeNotificationsResult(
    current$: ReplaySubject<Notification[]>,
    totalUnread$: ReplaySubject<number>,
    fetchNew: Observable<{ data: Notification[]; unreaded_number: number }>,
    fetchOld: Observable<{ data: Notification[]; unreaded_number: number }>,
    page: number
  ): Observable<Notification[]> {
    return forkJoin([fetchNew, fetchOld]).pipe(
      switchMap(([newRes, oldRes]) => {
        const all = [...newRes.data, ...oldRes.data].sort(
          (a, b) =>
            new Date(b.send_at as Date).getTime() -
            new Date(a.send_at as Date).getTime()
        );
        const totalUnread = newRes.unreaded_number + oldRes.unreaded_number;

        return current$.pipe(
          take(1),
          tap((existing) => {
            const merged =
              page === 1 || !existing || existing.length === 0
                ? all
                : [...existing, ...all];
            const sorted = merged.sort(
              (a, b) =>
                new Date(b.send_at as Date).getTime() -
                new Date(a.send_at as Date).getTime()
            );
            current$.next(sorted);
            totalUnread$.next(totalUnread);
          }),
          tap(() => {
            // console.log("Final emitted result:", all);
          }),
          map(() => all)
        );
      })
    );
  }

  private fetchNotificationsFromEndpoint(
    endpoint: string,
    page: number,
    size: number,
    show_unreaded: boolean,
    show_readed: boolean
  ): Observable<{
    has_more: boolean;
    data: Notification[];
    unreaded_number: number;
  }> {
    return resource(endpoint)
      .params({
        page: page,
        per_page: size,
        show_unreaded: show_unreaded,
        show_readed: show_readed,
      })
      .get<{
        data: Notification[];
        unreaded_number: number;
        has_more?: boolean;
      }>()
      .pipe(
        map((response) => ({
          ...response,
          has_more: response["has_more"] || false,
        }))
      );
  }

  get(notificationId: string): Observable<Notification> {
    if (this.USE_NEW_ENDPOINT) {
      return resource("crm://notification/with-id/id").params({ id: notificationId }).get<Notification>();
    } else {
      return resource("v2://host/push-notifications/id").params({ id: notificationId }).get<Notification>();
    }
  }

  markAllAsViewed() {
    const calls = [];
    if (this.USE_NEW_ENDPOINT) {
      calls.push(resource("crm://notification/read/all-notifications").put());
    }
    if (this.USE_OLD_ENDPOINT) {
      calls.push(
        resource("v2://host/push-notifications/mark-as-viewed").put({
          messages: false,
        })
      );
    }
    return forkJoin(calls).pipe(
      tap(() => {
        this.markAllViewedLocally(this.notification$, this.totalUnreadedNotifications$);
      })
    );
  }

  markAllAsViewedMex() {
    const calls = [];
    if (this.USE_NEW_ENDPOINT) {
      calls.push(resource("crm://notification/read/all-messages").put());
    }
    if (this.USE_OLD_ENDPOINT) {
      calls.push(
        resource("v2://host/push-notifications/mark-as-viewed").put({
          messages: false,
        })
      );
    }
    return forkJoin(calls).pipe(
      tap(() => {
        this.markAllViewedLocally(this.messages$, this.totalUnreadedMessages$);
      })
    );
  }

  markAsViewed(notificationId: string) {
    const calls = [];
    if (this.USE_NEW_ENDPOINT) {
      calls.push(
        resource("crm://notification/read")
          .params({
            id: notificationId,
          })
          .put()
      );
    }
    if (this.USE_OLD_ENDPOINT) {
      calls.push(
        resource("v2://host/push-notifications/mark-as-viewed-single").post({
          id: notificationId,
        })
      );
    }
    return forkJoin(calls).pipe(
      tap(() => {
        this.markViewedLocally(this.notification$, this.totalUnreadedNotifications$, notificationId);
        this.markViewedLocally(this.messages$, this.totalUnreadedMessages$, notificationId);
      })
    );
  }

  private markViewedLocally(
    list$: ReplaySubject<Notification[]>,
    unread$: ReplaySubject<number>,
    notificationId: string
  ): void {
    list$.pipe(take(1)).subscribe((list) => {
      if (!list?.length) {
        return;
      }
      let wasUnread = false;
      const next = list.map((item) => {
        if (item.id === notificationId && !item.viewed_at) {
          wasUnread = true;
          return { ...item, viewed_at: new Date().toISOString() };
        }
        return item;
      });
      list$.next(next);
      if (wasUnread) {
        unread$.pipe(take(1)).subscribe((count) => {
          unread$.next(Math.max(0, (count || 0) - 1));
        });
      }
    });
  }

  private markAllViewedLocally(
    list$: ReplaySubject<Notification[]>,
    unread$: ReplaySubject<number>
  ): void {
    list$.pipe(take(1)).subscribe((list) => {
      if (!list?.length) {
        unread$.next(0);
        return;
      }
      const now = new Date().toISOString();
      list$.next(list.map((item) => ({ ...item, viewed_at: item.viewed_at || now })));
      unread$.next(0);
    });
  }

  /**
   * Updates the in-memory messages list so banner rows with viewed_at are skipped,
   * and notifies listeners to collapse the top banner UI.
   */
  markMessageBannerDismissedLocally(notificationId: string): void {
    const idStr = String(notificationId);
    this.messages$.pipe(take(1)).subscribe((msgs) => {
      if (!msgs || !Array.isArray(msgs)) {
        this.messageBannerDismissedSubject.next(idStr);
        return;
      }
      const next = msgs.map((m) => {
        const mid = m.id != null ? String(m.id) : "";
        if (mid === idStr) {
          return { ...m, viewed_at: new Date().toISOString() } as Notification;
        }
        return m;
      });
      this.messages$.next(next);
      this.messageBannerDismissedSubject.next(idStr);
    });
  }
}
